import { DefaultButton } from '../../components';
import { Flex, TextStyled, StyledImg, Icon } from '../../component-library';
import { Text, theme } from '../../2022-component-library';
import { useEffect, useState } from 'react';
import Tape from '../../assets/img/measuring_tape_vertical.png';
import HorizontalTape from '../../assets/img/measuring_tape_horizontal.png';
import { useReceiveMessage, usePostMessage } from '../../context/messageContext';
import { keyframes } from '@emotion/react';
import { useCalibration } from '../../context/calibrationContext';

const HelpButton = ({ showHelpContent, setShowHelpContent }) => {
  return (
    <Flex style={{ position: 'absolute', top: '10px', right: '0.75rem', zIndex: 10 }}>
      <Icon
        icon={showHelpContent ? 'icon-close' : 'question'}
        color={showHelpContent ? theme.colors.negativeDark : '#fff'}
        size={30}
        onClick={() => setShowHelpContent(!showHelpContent)}
        hover
      />
    </Flex>
  );
};

const HelpContent = () => {
  return (
    <Flex
      width='100%'
      height='calc(100% - 50px)'
      flexDirection='column'
      justifyContent='center'
      alignItems='center'
      padding='1rem'
      bg='white'
      overflow='auto'
      style={{ position: 'absolute', top: '50px', zIndex: 9 }}
    >
      <Text marginBottom='1rem' textAlign='center'>
        {`Please measure the width and height of the screen, in mm, excluding bevel/edge.`}
      </Text>
      <Text>{`Enter your dimensions then click "Send Dimensions" to send to your clinician.`}</Text>
    </Flex>
  );
};

const fadeIn = keyframes`
0% { opacity: 0 }
100% { opacity: 1 }`;

const CalibrationRulerPatient = () => {
  const {
    calibrationData: {
      rulerDimensions: { widthMm, heightMm },
    },
  } = useCalibration();

  const [screenWidthInMm, setScreenWidthInMm] = useState(widthMm);
  const [screenHeightInMm, setScreenHeightInMm] = useState(heightMm);
  const [valuesSubmitted, setValuesSubmitted] = useState(false);
  const [showHelpContent, setShowHelpContent] = useState(false);
  const receivedMessage = useReceiveMessage();
  const postMessage = usePostMessage();

  const sendRulerDimensions = (rulerWidth, rulerHeight) => {
    const screenWidthPx = window.screen.width;
    const screenHeightPx = window.screen.height;
    const rulerDimensionsMessage = {
      type: 'rulerDimensions',
      content: {
        rulerWidth,
        rulerHeight,
        screenWidthPx,
        screenHeightPx,
      },
    };
    postMessage(rulerDimensionsMessage);
  };

  useEffect(() => {
    if (receivedMessage.type === 'rulerDimensions') setValuesSubmitted(true);
  }, [receivedMessage]);

  useEffect(() => {
    setValuesSubmitted(false);
  }, [screenHeightInMm, screenWidthInMm]);

  return (
    <Flex alignItems='center' justifyContent='center' flexDirection='column' height='100%'>
      <HelpButton showHelpContent={showHelpContent} setShowHelpContent={setShowHelpContent} />
      {showHelpContent && <HelpContent />}
      <StyledImg src={HorizontalTape} alt='ruler' style={{ left: 0, bottom: 0, position: 'fixed', width: '100%' }} />
      <StyledImg src={Tape} alt='ruler' style={{ position: 'fixed', top: '0', right: '0', width: 'auto', height: '100%' }} />
      <Flex mb='40px'>
        <TextStyled fontSize='22px'>To ensure accurate test results we need to know your screen size.</TextStyled>
      </Flex>
      <Flex mb='40px'>
        <Flex flexDirection='column' p='50px' alignItems='center'>
          <TextStyled fontSize='18px' mb='20px'>
            Measure Width
          </TextStyled>
          <TextStyled mb='20px'>Use the guide on the "bottom" to position your measuring tape.</TextStyled>
          <input
            onChange={(e) => setScreenWidthInMm(parseInt(e.target.value))}
            type='number'
            id='screen-width'
            name='screen-width'
            placeholder='Screen width (mm)'
            value={screenWidthInMm}
          />
        </Flex>
        <Flex>
          <Flex flexDirection='column' p='50px' alignItems='center'>
            <TextStyled fontSize='18px' mb='20px'>
              Measure Height
            </TextStyled>
            <TextStyled mb='20px'>Use the guide on the "right" to position your measuring tape.</TextStyled>
            <input
              onChange={(e) => setScreenHeightInMm(parseInt(e.target.value))}
              type='number'
              id='screen-height'
              name='screen-height'
              placeholder='Screen height (mm)'
              value={screenHeightInMm}
            />
          </Flex>
        </Flex>
      </Flex>
      <Flex height='20px'>
        {valuesSubmitted && (
          <Text fontWeight='bold' color={theme.colors.success} animation={`${fadeIn} 500ms forwards`}>{`Values submitted successfully.`}</Text>
        )}
      </Flex>
      <Flex>
        <DefaultButton
          text='Send dimensions'
          uppercase
          mt='20px'
          ml='15px'
          disabled={valuesSubmitted || !screenHeightInMm || !screenWidthInMm}
          onClick={() => sendRulerDimensions(screenWidthInMm, screenHeightInMm)}
        />
      </Flex>
    </Flex>
  );
};

export default CalibrationRulerPatient;
